/**
 * GlobalSMSNotifications - App-wide SMS notification handler
 * 
 * Shows modern toast notifications with customer info when new SMS messages arrive,
 * regardless of which page the user is on.
 * 
 * Uses polling (not WebSocket) for reliability.
 */
import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { MessageSquare, X, User, Phone, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const POLL_INTERVAL = 30000; // 30 seconds - increased for server stability

const GlobalSMSNotifications = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notificationPermission, setNotificationPermission] = useState('default');
  const prevTotalUnreadRef = useRef(null);
  const lastNotificationTimeRef = useRef(0);
  const recentMessagesRef = useRef([]);

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          setNotificationPermission(permission);
        });
      }
    }
  }, []);

  // Show browser notification
  const showBrowserNotification = useCallback((title, body) => {
    if (notificationPermission !== 'granted') return;

    try {
      const notification = new Notification(title, {
        body: body,
        icon: '/favicon.ico',
        tag: `sms-${Date.now()}`,
        requireInteraction: false,
        silent: false
      });

      notification.onclick = () => {
        window.focus();
        navigate('/crm');
        notification.close();
      };

      setTimeout(() => notification.close(), 5000);
    } catch (e) {
      console.warn('Browser notification failed:', e);
    }
  }, [notificationPermission, navigate]);

  // Modern toast notification with customer info
  const showToastNotification = useCallback((messages) => {
    // Get the most recent message for display
    const latestMessage = messages[0];
    const totalNew = messages.length;
    
    toast.custom((t) => (
      <div 
        className="animate-in slide-in-from-right-full duration-300 bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden cursor-pointer group hover:scale-[1.02] transition-transform"
        style={{ width: '380px' }}
        onClick={() => {
          navigate('/crm');
          toast.dismiss(t);
        }}
      >
        {/* Accent bar */}
        <div className="h-1 bg-gradient-to-r from-emerald-400 via-teal-500 to-cyan-500" />
        
        <div className="p-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
                  <MessageSquare className="h-5 w-5 text-white" />
                </div>
                {totalNew > 1 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-slate-900">
                    {totalNew > 9 ? '9+' : totalNew}
                  </span>
                )}
              </div>
              <div>
                <p className="text-sm font-semibold text-white">Nouveau message</p>
                <p className="text-[10px] text-slate-400">À l'instant</p>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                toast.dismiss(t);
              }}
              className="w-7 h-7 rounded-lg bg-slate-700/50 hover:bg-slate-600 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Customer Info */}
          {latestMessage && (
            <div className="bg-slate-800/50 rounded-xl p-3 mb-3 border border-slate-700/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                  <User className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {latestMessage.customer_name || 'Client'}
                  </p>
                  <div className="flex items-center gap-1.5 text-slate-400">
                    <Phone className="h-3 w-3" />
                    <span className="text-xs">{latestMessage.from_number || 'Numéro inconnu'}</span>
                  </div>
                </div>
              </div>
              
              {/* Message preview */}
              {latestMessage.preview && (
                <div className="mt-2 pt-2 border-t border-slate-700/50">
                  <p className="text-xs text-slate-300 line-clamp-2 italic">
                    "{latestMessage.preview}"
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Action button */}
          <button 
            className="w-full py-2.5 px-4 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white text-sm font-medium rounded-xl flex items-center justify-center gap-2 transition-all group-hover:shadow-lg group-hover:shadow-emerald-500/25"
          >
            <span>Voir la conversation</span>
            <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </button>

          {/* Multiple messages indicator */}
          {totalNew > 1 && (
            <p className="text-center text-[10px] text-slate-500 mt-2">
              +{totalNew - 1} autre{totalNew > 2 ? 's' : ''} message{totalNew > 2 ? 's' : ''} non lu{totalNew > 2 ? 's' : ''}
            </p>
          )}
        </div>
      </div>
    ), {
      duration: 10000,
      position: 'top-right',
    });
  }, [navigate]);

  // Poll for unread SMS count and recent messages
  useEffect(() => {
    if (!user) return;

    const fetchUnreadData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        // Fetch total unread count
        const countResponse = await fetch(`${BACKEND_URL}/api/sms/unread-total`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!countResponse.ok) return;

        const countData = await countResponse.json();
        const totalUnread = countData.total_unread || 0;

        // Check if we should show notification
        const now = Date.now();
        if (
          prevTotalUnreadRef.current !== null && 
          totalUnread > prevTotalUnreadRef.current &&
          (now - lastNotificationTimeRef.current) > 3000
        ) {
          const newCount = totalUnread - prevTotalUnreadRef.current;
          
          // Fetch recent messages to get customer info
          try {
            const recentResponse = await fetch(`${BACKEND_URL}/api/sms/recent-inbound?limit=${newCount}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (recentResponse.ok) {
              const recentData = await recentResponse.json();
              const messages = recentData.messages || [];
              
              if (messages.length > 0) {
                showToastNotification(messages);
                
                // Browser notification with customer name
                const latestMsg = messages[0];
                showBrowserNotification(
                  `💬 ${latestMsg.customer_name || 'Nouveau message'}`,
                  latestMsg.preview || `${newCount} nouveau${newCount > 1 ? 'x' : ''} message${newCount > 1 ? 's' : ''}`
                );
              } else {
                // Fallback if no message details
                showToastNotification([{ 
                  customer_name: 'Client', 
                  from_number: '', 
                  preview: '' 
                }]);
              }
            } else {
              // Fallback notification
              showToastNotification([{ 
                customer_name: 'Client', 
                from_number: '', 
                preview: `${newCount} nouveau${newCount > 1 ? 'x' : ''} message${newCount > 1 ? 's' : ''}` 
              }]);
            }
          } catch (err) {
            // Fallback notification on error
            showToastNotification([{ 
              customer_name: 'Client', 
              from_number: '', 
              preview: '' 
            }]);
          }
          
          lastNotificationTimeRef.current = now;
        }

        prevTotalUnreadRef.current = totalUnread;

      } catch (error) {
        console.error('Error fetching unread data:', error);
      }
    };

    // Initial fetch
    fetchUnreadData();

    // Set up polling interval
    const pollInterval = setInterval(fetchUnreadData, POLL_INTERVAL);

    return () => clearInterval(pollInterval);
  }, [user, showToastNotification, showBrowserNotification]);

  return <>{children}</>;
};

export default GlobalSMSNotifications;
